import React, { useState, useEffect } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Card } from "@progress/kendo-react-layout";
import { Loader } from "@progress/kendo-react-indicators";
import { analyzeSecurityThreats } from "./cohereAnalyst";

const AIAnalyst = ({ alerts }) => {
  const [analysis, setAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [nucliaResults, setNucliaResults] = useState([]);
  const [cohereStatus, setCohereStatus] = useState("");
  const [cohereRaw, setCohereRaw] = useState(null);
  const [cohereModel, setCohereModel] = useState("");

  const formatEnglishDateFull = (value) => {
    try {
      const d = new Date(value);
      if (isNaN(d)) return value ? String(value) : "";
      const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const wd = weekdays[d.getDay()];
      const mm = months[d.getMonth()];
      const dd = d.getDate();
      const yyyy = d.getFullYear();
      const h24 = d.getHours();
      const mi = String(d.getMinutes()).padStart(2, "0");
      const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
      const ampm = h24 < 12 ? "AM" : "PM";
      const tzMinutes = -d.getTimezoneOffset(); // east is positive
      const sign = tzMinutes >= 0 ? "+" : "-";
      const abs = Math.abs(tzMinutes);
      const tzhInt = Math.floor(abs / 60);
      const tzmInt = abs % 60;
      const tzDisplay =
        tzmInt === 0
          ? `GMT${sign}${tzhInt}`
          : `GMT${sign}${tzhInt}:${String(tzmInt).padStart(2, "0")}`;
      return `${wd}, ${mm} ${dd}, ${yyyy}, ${h12}:${mi} ${ampm} ${tzDisplay}`;
    } catch (_) {
      return value ? String(value) : "";
    }
  };

  const DateCellEnglish = (props) => {
    const { dataItem, field, tdProps, style, className, colSpan } = props;
    const baseTd = tdProps || {};
    const raw = dataItem?.[field] ?? dataItem?.date;
    const display = formatEnglishDateFull(raw);
    return (
      <td
        {...baseTd}
        style={{ ...(style || baseTd.style), whiteSpace: "nowrap" }}
        className={["ai-date-col", className, baseTd.className]
          .filter(Boolean)
          .join(" ")}
        colSpan={colSpan || baseTd.colSpan}
        title={display}
      >
        {display}
      </td>
    );
  };

  // Simulate Nuclia RAG analysis
  const simulateNucliaAnalysis = () => {
    const simulations = [
      "Nuclia: Scanning threat patterns in knowledge base...",
      "Nuclia: Comparing with known CVEs and vulnerabilities",
      "Nuclia: Analyzing behavioral patterns across 50K+ incidents",
      "Nuclia: Cross-referencing with real-time threat intelligence",
      "Nuclia: Generating AI-powered security recommendations",
    ];

    simulations.forEach((message, index) => {
      setTimeout(() => {
        setNucliaResults((prev) => [...prev, message]);
      }, index * 800);
    });

    return simulations;
  };

  const analyzeAlerts = async () => {
    setIsAnalyzing(true);
    setNucliaResults([]);
    setCohereStatus("");
    setAnalysis(null);

    // Start Nuclia RAG simulation for UX feedback
    simulateNucliaAnalysis();

    try {
      // Call Cohere analysis
      const ai = await analyzeSecurityThreats(alerts);
      const modelUsed = ai?._modelUsed || "";
      if (modelUsed) setCohereModel(modelUsed);
      setCohereRaw(ai);

      const highCount = alerts.filter((a) => a.severity === "High").length;
      const mediumCount = alerts.filter((a) => a.severity === "Medium").length;
      const lowCount = alerts.filter((a) => a.severity === "Low").length;

      const score10 = Math.max(1, Math.min(10, Number(ai.riskScore) || 7));
      const riskScore = Math.min(100, score10 * 10);
      const riskLevel =
        riskScore > 70
          ? "Critical"
          : riskScore > 40
          ? "High"
          : riskScore > 20
          ? "Medium"
          : "Low";
      const riskColor =
        riskScore > 70
          ? "#dc3545"
          : riskScore > 40
          ? "#ff6b35"
          : riskScore > 20
          ? "#ffc107"
          : "#28a745";

      const recs = Array.isArray(ai.recommendations) ? ai.recommendations : [];
      const recommendations = recs.slice(0, 6).map((r, i) => {
        const txt = typeof r === "string" ? r : JSON.stringify(r);
        const lower = txt.toLowerCase();
        const isHigh = /immediate|isolate|critical|block|contain/.test(lower);
        return {
          id: i + 1,
          action: txt,
          priority: isHigh ? "High" : "Medium",
          impact: isHigh ? "High" : "Medium",
          timeframe: isHigh ? "Immediate" : "48h",
        };
      });

      // Build prioritized alerts: cohere criticalThreats first (by order), then others by severity
      const criticalList = Array.isArray(ai.criticalThreats)
        ? ai.criticalThreats
        : [];
      const titleSet = new Set(criticalList);
      const critAlerts = alerts.filter((a) => a && titleSet.has(a.title));

      // Keep the order from criticalList only if it exists
      if (criticalList.length > 0) {
        critAlerts.sort(
          (a, b) =>
            criticalList.indexOf(a.title) - criticalList.indexOf(b.title)
        );
      }

      const nonCrit = alerts.filter((a) => a && !titleSet.has(a.title));
      const priorityOrder = { High: 3, Medium: 2, Low: 1 };
      nonCrit.sort(
        (a, b) =>
          (priorityOrder[b.severity] || 0) - (priorityOrder[a.severity] || 0)
      );

      const prioritized = [...critAlerts, ...nonCrit].map((a) => ({
        ...a,
        displayDate: formatEnglishDateFull(a?.date),
      }));

      const analysisResult = {
        riskScore,
        riskLevel,
        riskColor,
        cohereModel: modelUsed || process.env.REACT_APP_COHERE_MODEL || "",
        summary: {
          total: alerts.length,
          high: highCount,
          medium: mediumCount,
          low: lowCount,
        },
        recommendations: recommendations.length
          ? recommendations
          : [
              {
                id: 1,
                action: "Isolate critical systems",
                priority: "High",
                impact: "Critical",
                timeframe: "Immediate",
              },
              {
                id: 2,
                action: "Review firewall rules",
                priority: "High",
                impact: "High",
                timeframe: "24h",
              },
            ],
        prioritizedAlerts: prioritized,
      };

      setAnalysis(analysisResult);
      setCohereStatus("Cohere: Analysis complete via Chat API");
      setNucliaResults((prev) => [
        ...prev,
        "🔍 Cohere: Analysis complete via Chat API",
      ]);
    } catch (err) {
      // Safety fallback to local heuristic if something unexpected happens
      const highCount = alerts.filter((a) => a.severity === "High").length;
      const mediumCount = alerts.filter((a) => a.severity === "Medium").length;
      const lowCount = alerts.filter((a) => a.severity === "Low").length;
      const riskScore = Math.min(
        100,
        highCount * 40 + mediumCount * 20 + lowCount * 5
      );
      const riskLevel =
        riskScore > 70
          ? "Critical"
          : riskScore > 40
          ? "High"
          : riskScore > 20
          ? "Medium"
          : "Low";
      const riskColor =
        riskScore > 70
          ? "#dc3545"
          : riskScore > 40
          ? "#ff6b35"
          : riskScore > 20
          ? "#ffc107"
          : "#28a745";

      setAnalysis({
        riskScore,
        riskLevel,
        riskColor,
        summary: {
          total: alerts.length,
          high: highCount,
          medium: mediumCount,
          low: lowCount,
        },
        recommendations: [
          {
            id: 1,
            action: "Isolate critical systems",
            priority: "High",
            impact: "Critical",
            timeframe: "Immediate",
          },
          {
            id: 2,
            action: "Review firewall rules",
            priority: "High",
            impact: "High",
            timeframe: "24h",
          },
        ],
        prioritizedAlerts: [...alerts]
          .sort((a, b) => {
            const priorityOrder = { High: 3, Medium: 2, Low: 1 };
            return (
              (priorityOrder[b.severity] || 0) -
              (priorityOrder[a.severity] || 0)
            );
          })
          .map((a) => ({ ...a, displayDate: formatEnglishDateFull(a?.date) })),
      });

      const statusMsg = `Cohere: ${
        err && err.message ? err.message : "unknown error"
      }. Using fallback.`;
      setCohereStatus(statusMsg);
      setNucliaResults((prev) => [...prev, `🔍 ${statusMsg}`]);
      setCohereRaw(null);
      setCohereModel("");
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div
      className="ai-analyst-container"
      style={{
        margin: "30px 0",
        padding: "25px",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        borderRadius: "12px",
        color: "white",
      }}
    >
      {/* Nuclia RAG Integration Section */}
      <div
        className="section-card"
        style={{
          background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
          border: "1px solid #475569",
          borderRadius: "12px",
          padding: "20px",
          marginBottom: "25px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            marginBottom: "15px",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              background: "linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "20px",
              fontWeight: "bold",
              color: "white",
            }}
          >
            N
          </div>

          <div>
            <h4 style={{ margin: 0, color: "#3b82f6" }}>NUCLIA RAG AI</h4>
            <p style={{ margin: 0, color: "#94a3b8", fontSize: "14px" }}>
              Powered by Agentic RAG Technology
            </p>
          </div>
        </div>

        <div
          style={{
            background: "rgba(30, 41, 59, 0.5)",
            padding: "15px",
            borderRadius: "8px",
            border: "1px solid #475569",
            minHeight: "120px",
          }}
        >
          <p
            style={{ margin: "0 0 12px 0", color: "#cbd5e1", fontSize: "14px" }}
          >
            <strong>🔍 Real-time RAG Analysis:</strong> This feature utilizes
            Nuclia's Agentic RAG to analyze security alerts against our
            cybersecurity knowledge base.
          </p>

          {cohereStatus && (
            <div
              style={{
                margin: "10px 0 12px 0",
                padding: "10px 12px",
                paddingLeft: 0,
                borderRadius: "6px",
                background: "rgba(34,197,94,0.12)",
                border: "1px solid rgba(34,197,94,0.35)",
                color: "#cbd5e1",
                fontSize: "14px",
              }}
            >
              🔍 {cohereStatus}
            </div>
          )}

          {cohereStatus && cohereStatus.includes("error") && (
            <div
              style={{
                background: "#fee2e2",
                border: "1px solid #ef4444",
                color: "#dc2626",
                padding: "10px",
                borderRadius: "6px",
                margin: "10px 0",
              }}
            >
              ⚠️ {cohereStatus}
            </div>
          )}

          {nucliaResults.length > 0 && (
            <div style={{ marginTop: "15px" }}>
              <p
                style={{
                  margin: "0 0 8px 0",
                  color: "#3b82f6",
                  fontSize: "12px",
                }}
              >
                LIVE ANALYSIS:
              </p>
              {nucliaResults.slice(-3).map((result, index) => (
                <div
                  key={index}
                  style={{
                    background: "rgba(59, 130, 246, 0.1)",
                    padding: "8px 12px",
                    margin: "5px 0",
                    borderRadius: "6px",
                    borderLeft: "3px solid #3b82f6",
                    fontSize: "13px",
                    color: "#e2e8f0",
                  }}
                >
                  📋 {result}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
        }}
      >
        <h2 style={{ marginTop: 0, display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: "15px", fontSize: "28px" }}>🤖</span>
          AI Security Analyst
        </h2>
      </div>

      <Button
        themeColor="primary"
        onClick={analyzeAlerts}
        disabled={isAnalyzing}
        className="run-analysis-button"
        style={{
          marginBottom: "25px",
          background: isAnalyzing ? "#6c757d" : "#ff6b35",
          border: "none",
          padding: "12px 24px",
          fontWeight: "bold",
          width: "100%",
          maxWidth: "400px",
          boxSizing: "border-box",
          display: "block",
        }}
      >
        {isAnalyzing ? (
          <span className="run-analysis-button-text">
            🔄 Analyzing with Cohere AI...
          </span>
        ) : (
          <span className="run-analysis-button-text">
            🚀 Run AI Security Analysis with Cohere
          </span>
        )}
      </Button>

      {isAnalyzing && (
        <span
          style={{
            marginLeft: "12px",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            color: "#cbd5e1",
          }}
          title="Analyzing…"
        >
          <Loader type="pulsing" size="small" />
          <span>Analyzing…</span>
        </span>
      )}

      {analysis && (
        <div>
          {/* Risk Cards */}
          <div
            style={{
              display: "flex",
              gap: "20px",
              marginBottom: "25px",
              flexWrap: "wrap",
            }}
          >
            <Card
              style={{
                padding: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                flex: "1",
                minWidth: "250px",
                borderRadius: "10px",
              }}
            >
              <h3 style={{ color: "#333", marginTop: 0 }}>Risk Level</h3>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: analysis.riskColor,
                  padding: "10px",
                  background: "#f8f9fa",
                  borderRadius: "8px",
                  textAlign: "center",
                }}
              >
                {analysis.riskLevel}
              </div>
            </Card>

            <Card
              style={{
                padding: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                flex: "1",
                minWidth: "250px",
                borderRadius: "10px",
              }}
            >
              <h3 style={{ color: "#333", marginTop: 0 }}>Risk Score</h3>
              <div
                style={{
                  fontSize: "32px",
                  fontWeight: "bold",
                  color: analysis.riskColor,
                  textAlign: "center",
                }}
              >
                {analysis.riskScore}/100
              </div>
            </Card>

            <Card
              style={{
                padding: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                flex: "1",
                minWidth: "250px",
                borderRadius: "10px",
              }}
            >
              <h3 style={{ color: "#333", marginTop: 0 }}>
                Alert Distribution
              </h3>
              <p style={{ color: "#666", margin: 0 }}>
                🚨 High: <strong>{analysis.summary.high}</strong>
                <br />
                ⚠️ Medium: <strong>{analysis.summary.medium}</strong>
                <br />
                ℹ️ Low: <strong>{analysis.summary.low}</strong>
              </p>
            </Card>
          </div>

          {/* Recommendations Grid */}
          <div
            className="ai-grid-light"
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              padding: "20px",
              borderRadius: "10px",
              marginBottom: "25px",
            }}
          >
            <h3
              style={{
                color: "#333",
                marginTop: 0,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span>Security Recommendations</span>
              {analysis && analysis.cohereModel && (
                <span
                  style={{
                    fontSize: "12px",
                    background: "rgba(48, 43, 99, 0.10)",
                    border: "1px solid rgba(48, 43, 99, 0.35)",
                    color: "#302b63",
                    padding: "2px 6px",
                    borderRadius: "999px",
                    whiteSpace: "nowrap",
                    position: "relative",
                    top: "3px",
                    display: "inline-block",
                  }}
                >
                  <strong>Real AI Analysis</strong>- Powered by Cohere{" "}
                  {analysis.cohereModel}
                </span>
              )}
            </h3>
            <Grid data={analysis.recommendations}>
              <GridColumn
                field="action"
                title="Recommended Action"
                width="250px"
              />
              <GridColumn field="priority" title="Priority" width="100px" />
              <GridColumn field="impact" title="Impact" width="100px" />
              <GridColumn field="timeframe" title="Timeframe" width="100px" />
            </Grid>
          </div>

          {/* Prioritized Alerts Grid */}
          <div
            className="ai-grid-light"
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              padding: "20px",
              borderRadius: "10px",
            }}
          >
            <h3
              style={{
                color: "#333",
                marginTop: 0,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span>Prioritized Alerts</span>
              {analysis && analysis.cohereModel && (
                <span
                  style={{
                    fontSize: "12px",
                    background: "rgba(48, 43, 99, 0.10)",
                    border: "1px solid rgba(48, 43, 99, 0.35)",
                    color: "#302b63",
                    padding: "2px 6px",
                    borderRadius: "999px",
                    whiteSpace: "nowrap",
                    position: "relative",
                    top: "3px",
                    display: "inline-block",
                  }}
                >
                  <strong>Real AI Analysis</strong>- Powered by Cohere{" "}
                  {analysis.cohereModel}
                </span>
              )}
            </h3>
            <Grid data={analysis.prioritizedAlerts}>
              <GridColumn field="title" title="Alert" width="300px" />
              <GridColumn field="severity" title="Priority" width="100px" />
              <GridColumn
                field="displayDate"
                title="Date"
                width="520px"
                cells={{ data: DateCellEnglish }}
              />
            </Grid>
          </div>

          {/* Cohere Analysis (Raw) */}
          <div
            id="cohere-analysis-section"
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              padding: "20px",
              borderRadius: "10px",
              marginTop: "25px",
            }}
          >
            {(() => {
              const env = (process.env.NODE_ENV || "development").toLowerCase(); // 'production' en prod Vercel
              const isProd = env === "production";
              const label = isProd ? "Prod" : "Dev";
              const envStyle = isProd
                ? {
                    background: "rgba(16, 185, 129, 0.12)",
                    border: "1px solid #10b981",
                    color: "#065f46",
                  }
                : {
                    background: "rgba(239, 68, 68, 0.12)",
                    border: "1px solid #ef4444",
                    color: "#991b1b",
                  };

              return (
                <h3
                  style={{
                    color: "#333",
                    marginTop: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  Cohere Analysis (Raw)
                  <span
                    style={{
                      fontSize: 12,
                      padding: "2px 8px",
                      borderRadius: 999,
                      ...envStyle,
                    }}
                  >
                    {label}
                  </span>
                  {cohereRaw?.mode && (
                    <span
                      style={{
                        fontSize: 12,
                        padding: "2px 8px",
                        borderRadius: 999,
                        border: "1px solid #999",
                        color: "#333",
                      }}
                    >
                      {cohereRaw.mode}
                    </span>
                  )}
                  {cohereRaw?._source && (
                    <span
                      style={{
                        fontSize: 12,
                        padding: "2px 8px",
                        borderRadius: 999,
                        border: "1px solid #999",
                        color: "#333",
                      }}
                    >
                      {cohereRaw._source}
                    </span>
                  )}
                </h3>
              );
            })()}

            <p style={{ margin: "6px 0 12px 0", color: "#555" }}>
              {cohereModel ? `Model: ${cohereModel}` : "Model: (not available)"}
            </p>

            {cohereRaw ? (
              <pre
                style={{
                  margin: 0,
                  padding: "12px",
                  background: "#0b1020",
                  color: "#e2e8f0",
                  borderRadius: "8px",
                  overflow: "auto",
                  maxHeight: 260,
                }}
              >
                {JSON.stringify(cohereRaw, null, 2)}
              </pre>
            ) : (
              <p style={{ color: "#777", margin: 0 }}>
                No raw Cohere payload (fallback or not executed).
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAnalyst;
